import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthService } from '@/core/services/auth.service';
import {
    AiChatConversationDetail,
    AiChatConversationSummary,
    AiChatMessage,
    AiChatStreamChunk,
    AiGuideDocument,
    SuggestedQuestionsDto
} from './data-access/ai-chat.model';

type SuggestedQuestionsApiResponse = SuggestedQuestionsDto | { data: SuggestedQuestionsDto };
type ConversationsApiResponse = AiChatConversationSummary[] | { data: AiChatConversationSummary[] };
type ConversationDetailApiResponse = AiChatConversationDetail | { data: AiChatConversationDetail };
type GuidesApiResponse = AiGuideDocument[] | { data: AiGuideDocument[] };

/**
 * Owns the "ask anything" chat: streamed answers over SSE and the role-scoped suggested-question
 * chips. The streaming call is deliberately made with native fetch(), not HttpClient — Angular's
 * HttpClient buffers the full response body, which defeats streaming, and EventSource can't POST a
 * body or send a custom Authorization header. The Bearer token is attached manually here for the
 * same reason (this one call bypasses tokenInterceptor), using the same AuthService accessor
 * NotificationsLiveService already uses for its SignalR connection.
 */
@Injectable({ providedIn: 'root' })
export class AiChatService {
    private readonly apiUrl = environment.urls.api;

    private readonly _http = inject(HttpClient);
    private readonly _authService = inject(AuthService);

    public readonly messages = signal<AiChatMessage[]>([]);
    public readonly isStreaming = signal<boolean>(false);
    public readonly conversationId = signal<string | null>(null);
    public readonly suggestedQuestions = signal<string[]>([]);
    /** Name of the tool currently being called, for a "checking your deals…" style indicator. Null when idle. */
    public readonly activeToolCall = signal<string | null>(null);
    public readonly error = signal<string | null>(null);

    public readonly conversations = signal<AiChatConversationSummary[]>([]);
    public readonly conversationsLoading = signal<boolean>(false);
    public readonly guides = signal<AiGuideDocument[]>([]);
    public readonly guidesLoading = signal<boolean>(false);

    public loadSuggestedQuestions(): void {
        this._http.get<SuggestedQuestionsApiResponse>(`${this.apiUrl}/ai-chat/suggested-questions`).subscribe({
            next: (res) => {
                const payload = ('data' in res ? res.data : res) as SuggestedQuestionsDto;
                this.suggestedQuestions.set(payload?.questions ?? []);
            },
            error: () => this.suggestedQuestions.set([])
        });
    }

    public loadRecentConversations(): void {
        this.conversationsLoading.set(true);
        this._http.get<ConversationsApiResponse>(`${this.apiUrl}/ai-chat/conversations`).subscribe({
            next: (res) => {
                const payload = ('data' in res ? res.data : res) as AiChatConversationSummary[];
                this.conversations.set(payload ?? []);
                this.conversationsLoading.set(false);
            },
            error: () => {
                this.conversations.set([]);
                this.conversationsLoading.set(false);
            }
        });
    }

    public loadGuides(): void {
        this.guidesLoading.set(true);
        this._http.get<GuidesApiResponse>(`${this.apiUrl}/ai-chat/guides`).subscribe({
            next: (res) => {
                const payload = ('data' in res ? res.data : res) as AiGuideDocument[];
                this.guides.set(payload ?? []);
                this.guidesLoading.set(false);
            },
            error: () => {
                this.guides.set([]);
                this.guidesLoading.set(false);
            }
        });
    }

    /** Resumes a past conversation — loads its full history and makes it the active thread. */
    public openConversation(id: number): void {
        if (this.isStreaming()) {
            return;
        }

        this._http.get<ConversationDetailApiResponse>(`${this.apiUrl}/ai-chat/conversations/${id}`).subscribe({
            next: (res) => {
                const payload = ('data' in res ? res.data : res) as AiChatConversationDetail;
                if (!payload) {
                    return;
                }
                this.messages.set(payload.messages ?? []);
                this.conversationId.set(String(payload.id));
                this.error.set(null);
            },
            error: () => this.error.set("Couldn't load that conversation.")
        });
    }

    public async sendMessage(text: string): Promise<void> {
        const trimmed = text.trim();
        if (!trimmed || this.isStreaming()) {
            return;
        }

        this.error.set(null);
        this.activeToolCall.set(null);
        this.messages.update((msgs) => [...msgs, { role: 'user', text: trimmed }, { role: 'assistant', text: '' }]);
        this.isStreaming.set(true);

        let assistantText = '';

        try {
            const response = await fetch(`${this.apiUrl}/ai-chat/ask`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${this._authService.getAccessToken() ?? ''}`
                },
                body: JSON.stringify({ message: trimmed, conversationId: this.conversationId() })
            });

            if (!response.ok) {
                this.error.set(response.status === 400 ? 'Message could not be sent.' : `Request failed (${response.status}).`);
                return;
            }
            if (!response.body) {
                this.error.set('The assistant did not respond.');
                return;
            }

            const nextConversationId = response.headers.get('X-Conversation-Id');
            if (nextConversationId) {
                this.conversationId.set(nextConversationId);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    break;
                }
                buffer += decoder.decode(value, { stream: true });

                let boundary: number;
                while ((boundary = buffer.indexOf('\n\n')) !== -1) {
                    const line = buffer.slice(0, boundary).trim();
                    buffer = buffer.slice(boundary + 2);
                    if (!line.startsWith('data: ')) {
                        continue;
                    }

                    let chunk: AiChatStreamChunk;
                    try {
                        chunk = JSON.parse(line.slice(6)) as AiChatStreamChunk;
                    } catch {
                        continue;
                    }

                    if (chunk.type === 'text_delta') {
                        this.activeToolCall.set(null);
                        assistantText += chunk.textDelta ?? '';
                        this.updateLastAssistantMessage(assistantText);
                    } else if (chunk.type === 'tool_call') {
                        this.activeToolCall.set(chunk.toolName);
                    } else if (chunk.type === 'error') {
                        this.activeToolCall.set(null);
                        this.error.set(chunk.errorMessage ?? 'The assistant could not answer that.');
                    } else if (chunk.type === 'done') {
                        this.activeToolCall.set(null);
                    }
                }
            }
        } catch {
            this.error.set('Something went wrong reaching the assistant.');
        } finally {
            this.isStreaming.set(false);
            this.activeToolCall.set(null);
        }

        // Refresh the sidebar list so a brand-new conversation (or a bumped-up existing one) shows up
        // without the user having to reopen the drawer.
        this.loadRecentConversations();
    }

    public resetConversation(): void {
        this.messages.set([]);
        this.conversationId.set(null);
        this.error.set(null);
        this.activeToolCall.set(null);
    }

    private updateLastAssistantMessage(text: string): void {
        this.messages.update((msgs) => {
            const lastIndex = msgs.length - 1;
            if (lastIndex < 0 || msgs[lastIndex].role !== 'assistant') {
                return msgs;
            }
            const copy = [...msgs];
            copy[lastIndex] = { ...copy[lastIndex], text };
            return copy;
        });
    }
}
